import { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import { AddEntityDrawerProps } from 'components/IAP/EntityListProps';
import FormCheckboxGroupField from 'components/IAP/formFields/FormCheckboxGroupField';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useAors } from 'hooks/useAors';
import {
  CreateNotificationFormFields,
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { useRosters } from 'hooks/useRosters';
import { Offcanvas } from 'react-bootstrap';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import ChannelMessageInput from './ChannelMessageInput';
import { useParams } from 'react-router';

export const notificationTypeOptions = [
  // In app is a forced selection
  {
    label: 'PRATUS',
    value: NotificationType.IN_APP,
    disabled: true,
  },
  {
    label: NotificationType.EMAIL,
    value: NotificationType.EMAIL,
  },
  {
    label: NotificationType.SMS.toUpperCase(),
    value: NotificationType.SMS,
  },
  {
    label: `${NotificationType.TEAMS}`,
    value: NotificationType.TEAMS,
  },
];

export const notificationPriorityOptions = Object.entries(
  NotificationPriority
).map(([key, value]) => ({
  label:
    key === NotificationPriority.CRITICAL
      ? 'Stand Down Safety'
      : key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
  value: value,
}));

enum DeliveryMode {
  NOW = 'now',
  SCHEDULE = 'schedule',
}

const NewNotificationDrawer: React.FC<AddEntityDrawerProps> = ({ toggle }) => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [deliveryMode, setDeliveryMode] = useState<
    DeliveryMode.SCHEDULE | DeliveryMode.NOW
  >(DeliveryMode.NOW);

  const form = useForm<CreateNotificationFormFields>({
    mode: 'onChange',
    defaultValues: {
      incidentId: incidentId ?? '',
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: '',
      emailMessage: '',
      smsMessage: '',
      teamsMessage: '',
      phoneMessage: '',
      appMessage: '',
      userIds: [],
      teamIds: [],
      sourceItemId: null,
      sourceItemType: null,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
    },
  });

  const { handleSubmit, control } = form;
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: rosters } = useRosters([groupGuid]);
  const { data: incidents } = useAllIncidents();
  const { data: aors } = useAors();
  const createNotificationMutation = useCreateNotification();

  const selectedPriority = useWatch({ control, name: 'priority' });

  const handleChannelChange = (
    selectedChannels: string | string[] | undefined
  ) => {
    const channels = selectedChannels as NotificationType[];

    if (!channels.includes(NotificationType.EMAIL)) {
      form.setValue('emailSubject', '');
      form.setValue('emailMessage', '');
    }
    if (!channels.includes(NotificationType.IN_APP)) {
      form.setValue('appMessage', '');
    }
    if (!channels.includes(NotificationType.TEAMS)) {
      form.setValue('teamsMessage', '');
    }
    if (!channels.includes(NotificationType.SMS)) {
      form.setValue('smsMessage', '');
    }
  };

  const onSubmit = async (formData: CreateNotificationFormFields) => {
    if (!formData.userIds || formData.userIds.length === 0) {
      form.setError('userIds', {
        message: 'Please select at least one individual',
      });
      return;
    }

    if (!formData.priority) {
      form.setError('priority', {
        message: 'Please select a severity',
      });
      return;
    }
    await createNotificationMutation.mutateAsync(formData);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Create Notification">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="d-flex flex-column h-100"
        >
          <Offcanvas.Body className="d-flex flex-column flex-grow-1">
            <FormCheckboxGroupField
              title="Communication Channels"
              fieldName="notificationTypes"
              options={notificationTypeOptions}
              onChangeCallback={handleChannelChange}
            />
            <div className="mb-3">
              <ChannelMessageInput />
            </div>
            <div className="mb-3">
              <Controller
                control={control}
                name="acknowledgementRequired"
                render={({ field }) => (
                  <StylishNewCheckbox
                    label="Let Recipients Acknowledge and Dismiss"
                    id="acknowledgementRequired"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <FormSelectField
              title="Recipients *"
              fieldName="userIds"
              options={
                rosters?.map((u) => ({
                  label: u.email_address,
                  value: u.user_guid,
                })) || []
              }
              variant="full"
              isMulti={true}
            />
            <FormSelectField
              title="Associated Incident"
              fieldName="incidentId"
              options={
                incidents?.map((u) => ({
                  label: u.name,
                  value: u.id,
                })) || []
              }
              variant="full"
              isClearable
              disabled={!!incidentId}
            />
            <FormSelectField
              title="Severity *"
              fieldName="priority"
              options={notificationPriorityOptions}
              variant="full"
              onChangeCallback={(value) => {
                if (value === NotificationPriority.CRITICAL) {
                  setDeliveryMode(DeliveryMode.NOW);
                  form.setValue('scheduledDate', null);
                }
              }}
            />
            <FormSelectField
              title="Location"
              fieldName="aors"
              options={
                aors?.map((u) => ({
                  label: u.name,
                  value: u.id,
                })) || []
              }
              variant="full"
              isMulti={true}
              isClearable
            />
            {selectedPriority !== NotificationPriority.CRITICAL && (
              <div className="mb-3">
                <StylishNewToggleButtonGroup
                  data={[
                    { label: 'Send now', value: DeliveryMode.NOW },
                    {
                      label: 'Schedule delivery',
                      value: DeliveryMode.SCHEDULE,
                    },
                  ]}
                  selected={deliveryMode}
                  onChange={(value) => {
                    setDeliveryMode(
                      value as DeliveryMode.SCHEDULE | DeliveryMode.NOW
                    );
                    if (value === DeliveryMode.NOW) {
                      form.setValue('scheduledDate', null);
                    }
                  }}
                  onBlur={() => {}}
                />
              </div>
            )}
            {deliveryMode === DeliveryMode.SCHEDULE &&
              selectedPriority !== NotificationPriority.CRITICAL && (
                <FormDateTimeField
                  title="Scheduled Date"
                  fieldName="scheduledDate"
                />
              )}
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={createNotificationMutation.isPending}
              >
                {deliveryMode === DeliveryMode.SCHEDULE ? 'Schedule' : 'Send'}
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default NewNotificationDrawer;
